/**
 * Event shifts: dated time slots on an event, each with an optional headcount.
 *
 * Shifts live in `public.event_shifts`; who works which one lives in
 * `public.volunteer_shifts`. See supabase/migrations/20260917180000_event_shifts.sql and
 * docs/event-shifts-plan.md.
 *
 * Everything here is pure. Dates and times are the device's local time: an organizer picks
 * "Sat 14, 09:00" on their phone and that instant is what gets stored.
 */

/** A shift as returned by `get_event_shifts`. */
export interface EventShift {
  id: string;
  starts_at: string;
  ends_at: string;
  /** null = no limit. */
  capacity: number | null;
  /** How many volunteers are on it. */
  taken: number;
  /** Whether the signed-in user is on it. */
  mine: boolean;
}

/** A shift being edited in the event form. Saved shifts carry their id. */
export interface ShiftDraft {
  /** Stable React key: the id for saved shifts, a local key for new ones. */
  key: string;
  id: string | null;
  starts_at: string;
  ends_at: string;
  capacity: number | null;
  /** Volunteers already on it (0 for unsaved shifts). */
  taken: number;
}

export const MAX_SHIFTS_PER_EVENT = 100;
export const MAX_SHIFT_CAPACITY = 100;

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** 'YYYY-MM-DD' of a date in local time. */
export function toDayKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Local day key of an event date as the form holds it. The date picker hands back a bare
 * 'YYYY-MM-DD', which `new Date()` would read as UTC midnight -- the previous day anywhere
 * west of UTC -- so that form is taken as-is. A stored timestamp is read in local time.
 */
export function eventDateToDayKey(value: string | null | undefined): string | null {
  if (!value) return null;
  if (DAY_KEY_RE.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : toDayKey(date);
}

/** Local instant for a day key and 'HH:mm'. */
export function localDateTime(dayKey: string, time: string): Date {
  const [y, m, d] = dayKey.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

/** 'HH:mm' of an instant in local time. */
export function toLocalTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Every day key from start to end, inclusive. Empty when end is before start. */
export function eachDayInRange(startKey: string, endKey: string): string[] {
  const days: string[] = [];
  const cursor = localDateTime(startKey, '00:00');
  const end = localDateTime(endKey, '00:00');
  // Stepping with setDate keeps this right across daylight-saving changes, where a day is
  // not 24 hours long.
  while (cursor <= end) {
    days.push(toDayKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/** '09:00 – 12:00' */
export function formatShiftTime(shift: Pick<EventShift, 'starts_at' | 'ends_at'>): string {
  return `${toLocalTime(shift.starts_at)} – ${toLocalTime(shift.ends_at)}`;
}

/** 'Sat, Sep 14' */
export function formatShiftDay(dayKey: string): string {
  return localDateTime(dayKey, '00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** 'Sat, Sep 14 · 09:00 – 12:00' */
export function formatShiftLabel(shift: Pick<EventShift, 'starts_at' | 'ends_at'>): string {
  return `${formatShiftDay(toDayKey(new Date(shift.starts_at)))} · ${formatShiftTime(shift)}`;
}

export interface ShiftDay<T> {
  dayKey: string;
  label: string;
  shifts: T[];
}

/** Shifts grouped by the local day they start on, days and shifts in time order. */
export function groupShiftsByDay<T extends Pick<EventShift, 'starts_at' | 'ends_at'>>(
  shifts: T[],
): ShiftDay<T>[] {
  const sorted = [...shifts].sort(compareShifts);
  const days: ShiftDay<T>[] = [];
  for (const shift of sorted) {
    const dayKey = toDayKey(new Date(shift.starts_at));
    const last = days[days.length - 1];
    if (last && last.dayKey === dayKey) {
      last.shifts.push(shift);
    } else {
      days.push({ dayKey, label: formatShiftDay(dayKey), shifts: [shift] });
    }
  }
  return days;
}

export function compareShifts(
  a: Pick<EventShift, 'starts_at' | 'ends_at'>,
  b: Pick<EventShift, 'starts_at' | 'ends_at'>,
): number {
  return (
    new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime() ||
    new Date(a.ends_at).getTime() - new Date(b.ends_at).getTime()
  );
}

/** Spots left, or null when the shift has no limit. Never negative. */
export function shiftSpotsLeft(shift: Pick<EventShift, 'capacity' | 'taken'>): number | null {
  if (shift.capacity == null) return null;
  return Math.max(0, shift.capacity - shift.taken);
}

/** Has the shift already begun? Leaving is only offered before that (see `leave_shift`). */
export function hasShiftStarted(shift: Pick<EventShift, 'starts_at'>, now: Date = new Date()): boolean {
  return new Date(shift.starts_at).getTime() <= now.getTime();
}

/** Do two shifts share any time? Back-to-back shifts (one ends as the next starts) do not. */
export function shiftsOverlap(
  a: Pick<EventShift, 'starts_at' | 'ends_at'>,
  b: Pick<EventShift, 'starts_at' | 'ends_at'>,
): boolean {
  return (
    new Date(a.starts_at).getTime() < new Date(b.ends_at).getTime() &&
    new Date(b.starts_at).getTime() < new Date(a.ends_at).getTime()
  );
}

export interface ShiftChangeOptions {
  /** Shifts the volunteer could move to, in time order. Full ones are included, to show. */
  options: EventShift[];
  /** shiftId -> "Overlaps your 12:00 – 15:00", for options clashing with another of theirs. */
  notes: Record<string, string>;
}

/**
 * Where a volunteer can move a shift to: the event's other shifts that have not started and
 * that they are not already on. Mirrors the checks in `change_shift`, which is the authority --
 * this only decides what to offer. Overlaps are flagged, not removed: the shift being left
 * does not count, since the volunteer will no longer be on it.
 */
export function shiftChangeOptions(
  shifts: EventShift[],
  fromShiftId: string,
  now: Date = new Date(),
): ShiftChangeOptions {
  const keeping = shifts.filter((s) => s.mine && s.id !== fromShiftId);
  const options = shifts
    .filter((s) => s.id !== fromShiftId && !s.mine && !hasShiftStarted(s, now))
    .sort(compareShifts);
  const notes: Record<string, string> = {};
  for (const option of options) {
    const clash = keeping.find((mine) => shiftsOverlap(option, mine));
    if (clash) notes[option.id] = `Overlaps your ${formatShiftTime(clash)}`;
  }
  return { options, notes };
}

/**
 * Shifts a volunteer already on the event can add themselves to: ones that have not started
 * and that they are not on yet. Full ones are included so they can be shown as full.
 * Mirrors `join_shifts`, which is the authority. Overlaps with the volunteer's current shifts
 * are flagged, not removed.
 */
export function shiftJoinOptions(shifts: EventShift[], now: Date = new Date()): ShiftChangeOptions {
  const mine = shifts.filter((s) => s.mine);
  const options = shifts.filter((s) => !s.mine && !hasShiftStarted(s, now)).sort(compareShifts);
  const notes: Record<string, string> = {};
  for (const option of options) {
    const clash = mine.find((m) => shiftsOverlap(option, m));
    if (clash) notes[option.id] = `Overlaps your ${formatShiftTime(clash)}`;
  }
  return { options, notes };
}

export function isShiftFull(shift: Pick<EventShift, 'capacity' | 'taken'>): boolean {
  return shiftSpotsLeft(shift) === 0;
}

/** '2 of 5 spots left' / 'Full' / '4 signed up' / 'No limit' */
export function formatShiftAvailability(shift: Pick<EventShift, 'capacity' | 'taken'>): string {
  const left = shiftSpotsLeft(shift);
  if (left === null) {
    return shift.taken > 0 ? `${shift.taken} signed up` : 'No limit';
  }
  if (left === 0) return 'Full';
  return `${left} of ${shift.capacity} ${shift.capacity === 1 ? 'spot' : 'spots'} left`;
}

/** Local key for a shift that has not been saved yet. */
export function draftKey(startsAt: string, endsAt: string): string {
  return `new:${startsAt}|${endsAt}`;
}

export function shiftToDraft(shift: EventShift): ShiftDraft {
  return {
    key: shift.id,
    id: shift.id,
    starts_at: shift.starts_at,
    ends_at: shift.ends_at,
    capacity: shift.capacity,
    taken: shift.taken,
  };
}

export interface NewShiftInput {
  /** The days to add the shift on -- one, or every day of the event. */
  days: string[];
  startTime: string;
  endTime: string;
  capacity: number | null;
}

/**
 * Drafts for a shift added in the form, or an error message.
 * Days the exact same shift already exists on are skipped rather than rejected, so
 * "every day" can be used to fill in the days that are still missing.
 */
export function buildNewShifts(
  existing: ShiftDraft[],
  input: NewShiftInput,
): { drafts: ShiftDraft[]; error: null } | { drafts: null; error: string } {
  if (input.days.length === 0) return { drafts: null, error: 'Please choose a day for the shift.' };
  if (!TIME_RE.test(input.startTime) || !TIME_RE.test(input.endTime)) {
    return { drafts: null, error: 'Please select both start and end time.' };
  }
  if (input.endTime <= input.startTime) {
    return { drafts: null, error: 'End time must be after start time.' };
  }
  if (
    input.capacity !== null &&
    (!Number.isInteger(input.capacity) || input.capacity < 1 || input.capacity > MAX_SHIFT_CAPACITY)
  ) {
    return { drafts: null, error: `Spots must be a whole number from 1 to ${MAX_SHIFT_CAPACITY}.` };
  }

  const taken = new Set(existing.map((s) => shiftIdentity(s.starts_at, s.ends_at)));
  const drafts: ShiftDraft[] = [];
  for (const day of input.days) {
    const startsAt = localDateTime(day, input.startTime).toISOString();
    const endsAt = localDateTime(day, input.endTime).toISOString();
    const identity = shiftIdentity(startsAt, endsAt);
    if (taken.has(identity)) continue;
    taken.add(identity);
    drafts.push({
      key: draftKey(startsAt, endsAt),
      id: null,
      starts_at: startsAt,
      ends_at: endsAt,
      capacity: input.capacity,
      taken: 0,
    });
  }

  if (drafts.length === 0) {
    return { drafts: null, error: 'This shift has already been added.' };
  }
  if (existing.length + drafts.length > MAX_SHIFTS_PER_EVENT) {
    return { drafts: null, error: `An event can have at most ${MAX_SHIFTS_PER_EVENT} shifts.` };
  }
  return { drafts, error: null };
}

/** Same shift regardless of how the timestamp string is formatted. */
function shiftIdentity(startsAt: string, endsAt: string): string {
  return `${new Date(startsAt).getTime()}|${new Date(endsAt).getTime()}`;
}

/** Shifts that start before the event's first day or after its last. */
export function shiftsOutsideEventDates(
  drafts: ShiftDraft[],
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): ShiftDraft[] {
  const first = eventDateToDayKey(startDate);
  const last = eventDateToDayKey(endDate);
  if (!first || !last) return [];
  return drafts.filter((d) => {
    const day = toDayKey(new Date(d.starts_at));
    return day < first || day > last;
  });
}

export interface ShiftChanges {
  toInsert: ShiftDraft[];
  toDelete: string[];
  toUpdateCapacity: { id: string; capacity: number | null }[];
}

/** What saving the form has to write, given the shifts it was opened with. */
export function diffShifts(original: ShiftDraft[], current: ShiftDraft[]): ShiftChanges {
  const currentById = new Map(current.filter((d) => d.id).map((d) => [d.id as string, d]));
  const toDelete = original.filter((o) => o.id && !currentById.has(o.id)).map((o) => o.id as string);
  const toUpdateCapacity = original.flatMap((o) => {
    const now = o.id ? currentById.get(o.id) : undefined;
    return now && now.capacity !== o.capacity ? [{ id: now.id as string, capacity: now.capacity }] : [];
  });
  const toInsert = current.filter((d) => !d.id);
  return { toInsert, toDelete, toUpdateCapacity };
}

export function hasShiftChanges(changes: ShiftChanges): boolean {
  return changes.toInsert.length > 0 || changes.toDelete.length > 0 || changes.toUpdateCapacity.length > 0;
}
